import App from '@/app/App'
import { Pills } from '@/app/Pills'
import { Word } from '@/app/Word'
import { BodyColor } from '@/const'
import { TitleSlice } from '../../prismicio-types'
import { asHTML, asText } from '@prismicio/client'
import styles from './Title.module.css'
import linkResolver from '../linkResolver'

enum TYPES {
	heading1 = 'h1',
	heading2 = 'h2',
	heading3 = 'h3',
	heading4 = 'h4',
	heading5 = 'h5',
	heading6 = 'h6'
}
const DESCENDERS = ['q', 'p', 'g', 'j', 'y']
const CHAR_WIDTH = 0.6
const PADDING = 0.7

type Options = {
	node: HTMLElement
	app: App
	color: BodyColor
}

function getMaxFontSize(text: string) {
	const longestWord = text.split(/\s+/).reduce((max, word) => Math.max(max, word.length), 0)

	if (!longestWord) return null

	return 100 / (longestWord * CHAR_WIDTH + PADDING)
}

export class Title {
	private readonly node: HTMLElement
	private readonly app: App
	private readonly words: Word[] = []
	private readonly pills: Pills

	constructor({ node, app, color }: Options) {
		this.onIntersect = this.onIntersect.bind(this)
		this.node = node
		this.app = app

		const lines = Array.from(this.node.querySelectorAll(`.${styles.Line}`)) as HTMLElement[]

		this.words = []
		lines.forEach((line) => {
			const words = Array.from(line.querySelectorAll(`.${styles.Word}`)) as HTMLElement[]
			const text = words.reduce((text, word) => {
				text += word.textContent
				return text
			}, '')
			const hasDescender = DESCENDERS.some((desc) => text.includes(desc))
			words.forEach((word) => this.words.push(new Word(word, hasDescender)))
		})

		this.pills = new Pills({ app: app, color, num: this.words.length })
		this.app.on('intersect', this.onIntersect)
	}

	async onIntersect(entries: IntersectionObserverEntry[]) {
		for (let i = 0; i < entries.length; i++) {
			if (entries[i].isIntersecting && entries[i].target === this.node) {
				this.showTitle()
				this.app.intersectionObserver.unobserve(this.node)
				break
			}
		}
	}

	async showTitle() {
		await this.pills.show()
		this.words.forEach((word) => word.show())
	}

	async show() {
		this.app.intersectionObserver.observe(this.node)
	}

	dispose() {
		this.pills.dispose()
		this.words.forEach((word) => word.dispose())
		this.app.removeListener('intersect', this.onIntersect)
		this.app.intersectionObserver.unobserve(this.node)
	}

	resize() {
		const { width, height, left, top } = this.node.getBoundingClientRect()
		const aspect = height / width
		this.pills.resize({ aspect, width, height, left, top, line: 2 })

		this.words.forEach((word, index) => {
			word.resize(width, height, left, top, aspect)
			const { p1, p2, radius } = word
			this.pills.resizePill({ index, p1, p2, radius })
		})

		this.app.requestRender()
	}

	update() {
		const { height: wHeight } = this.app.sizes
		const { top, bottom, height } = this.node.getBoundingClientRect()

		if (bottom < 0 || top > wHeight) {
			this.pills.visible = false
			return
		}

		this.pills.visible = true
		const y = (top + height * 0.5 - wHeight * 0.5) / (wHeight * 0.5)
		this.pills.update(y)
	}

	static render(props: TitleSlice) {
		const {
			primary: { title, small }
		} = props

		const maxFontSize = small ? null : getMaxFontSize(asText(title))

		const serializer = (type: string, element: any) => {
			if (Object.keys(TYPES).includes(type)) {
				const lines = element.text.trim().split('\n')

				return /* HTML */ `
					<${TYPES[type as keyof typeof TYPES]}>
						${lines
							.map((line: string) => {
								const words = line.trim().split(' ')
								return `<span class="${styles.Line}">${words
									.map((word) => `<span class="${styles.Word}">${word}</span>`)
									.join(' ')}</span>`
							})
							.join('<br/>')}
					</${TYPES[type as keyof typeof TYPES]}>
				`
			}

			return null
		}

		return /* HTML */ `
			<div
				class="${styles.Main} ${small ? styles.Small : ''}"
				${maxFontSize ? `style="--max-font-size: ${maxFontSize.toFixed(2)}vw"` : ''}
			>
				<div class="${styles.Title}">${asHTML(title, { linkResolver, serializer })}</div>
			</div>
		`
	}
}
